import React, { useMemo, useState } from "react";
import Select from "react-select";
import { FaSearch } from "react-icons/fa";
import styles from "./SearchForm.module.css";
import { CATEGORY_LABELS } from "./constants";
import { analyseInput, getInputMessage } from "./parseInput";

// domain dropdown + values textarea + search button
//   onSearch({ domain, input }) is called only when the form is valid
export default function SearchForm({ domainOptions, loading, onSearch }) {
  const [domain, setDomain] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [errors, setErrors] = useState({});

  const input = useMemo(() => analyseInput(searchValue), [searchValue]);
  const isValid = input.status === "ok";

  const handleSearch = () => {
    const errs = {};
    if (!domain) errs.domain = "Select a domain.";
    if (!isValid) errs.value = getInputMessage(input);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    onSearch({ domain, input });
  };

  return (
    <div className={`container-xxl pt-3 ${styles.searchRow}`}>
      <div className="row g-3 align-items-start">
        <div className="col-lg-3">
          <Select
            value={domain}
            onChange={(v) => {
              setDomain(v);
              setErrors((e) => ({ ...e, domain: undefined }));
            }}
            options={domainOptions}
            placeholder="Select domain..."
            isClearable
            classNamePrefix="tp-select"
            styles={{
              control: (b) => ({
                ...b,
                borderColor: errors.domain ? "#dc2626" : b.borderColor,
              }),
            }}
          />
          {errors.domain && <div className="text-danger small mt-1">{errors.domain}</div>}
        </div>

        <div className="col-lg-5 ms-2">
          <div className="input-group has-validation">
            <textarea
              id="tp-value"
              className={`form-control ${errors.value ? "is-invalid" : ""}`}
              placeholder={"Paste Ref IDs (123,219,456), ranges (123-456) or Clean MD5s\n(one per line, or comma separated)"}
              value={searchValue}
              style={{
                minHeight: "48px",
                resize: "none",
                overflowY: "hidden",
              }}
              onInput={(e) => {
                e.currentTarget.style.height = "48px";
                e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
              }}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setErrors((f) => ({ ...f, value: undefined }));
              }}
              onKeyDown={(e) => {
                // Ctrl/Cmd + Enter = search (plain Enter adds a new line)
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSearch();
              }}
            />
            {errors.value && (
              <div className="invalid-feedback d-block">{errors.value}</div>
            )}
          </div>

          {!errors.value && (
            <div className="form-text">
              {input.status === "empty" ? (
                "Numbers and ranges (123-456) are searched as Ref ID, 32 characters as Clean MD5."
              ) : (
                <span
                  className={`badge ${isValid
                    ? "text-bg-success"
                    : input.status === "partial"
                      ? "text-bg-secondary"
                      : "text-bg-danger"
                    }`}
                >
                  {isValid
                    ? `${input.values.length} ${CATEGORY_LABELS[input.type]}${input.values.length > 1 ? "s" : ""
                    }`
                    : getInputMessage(input)}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="col-lg-2">
          <button
            type="button"
            className={`btn ${styles.searchButton} text-light fs-5 w-100`}
            onClick={handleSearch}
            disabled={loading || !isValid}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                Searching
              </>
            ) : (
              <>
                <FaSearch size={20} className="me-2" />
                Search
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
